import { useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography, Alert } from '@mui/material';
import { apiClient } from '../providers/apiClient';
import { logger } from '../utils/logger';

interface ComparisonSide {
  output: string | null; unavailable: string | null; error: string | null; model: string | null;
  latencyMs: number; inputTokens: number | null; outputTokens: number | null; costUSD: number | null;
}
interface ComparisonReport { baseline: ComparisonSide; current: ComparisonSide }

function Answer({ value }: { value: string }) {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed.suggestions)) return <>{parsed.suggestions.map((idea: { title: string; suggestion: string; reasoning: string }, index: number) =>
      <Box key={index} mb={2}><Typography fontWeight={600}>{idea.title}</Typography>
        <Typography>{idea.suggestion}</Typography><Typography color="text.secondary" mt={1}>{idea.reasoning}</Typography></Box>)}</>;
    if (parsed.emotional && parsed.work && parsed.health) return <>
      <Typography fontWeight={600} mb={1}>{parsed.title}</Typography>
      {['emotional', 'work', 'health'].map(key => <Box key={key} mb={2}>
        <Typography fontWeight={600}>{parsed[key].title}</Typography><Typography>{parsed[key].content}</Typography></Box>)}
    </>;
  } catch { /* Chat answers are plain text. */ }
  return <Typography sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>;
}

export function ContextValidationPanel() {
  const [scenario, setScenario] = useState('living_apart');
  const [feature, setFeature] = useState('suggestions');
  const [tier, setTier] = useState('flame');
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<ComparisonReport | null>(null);
  const [error, setError] = useState(false);
  return <Box mt={3}>
    <Typography variant="subtitle1">Compare use of user information</Typography>
    <Typography color="text.secondary" mb={2}>
      Fixed synthetic people, published baseline versus current prompts, using the same selected tier model.
      JEV is bypassed. This uses provider tokens without saving suggestions, portraits, or charging user hearts.
      Review the answers for accuracy and usefulness; a completed request does not prove quality improved.
    </Typography>
    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
      <TextField select disabled={running} size="small" label="Scenario" value={scenario} onChange={e => { setScenario(e.target.value); setReport(null); setError(false); }} sx={{ minWidth: 180 }}>
        {Object.entries({ living_apart: 'Living far apart', parenting: 'Young children', conflicting_answers: 'Conflicting answers',
          questionnaire_only: 'Questionnaire only', limited_cards: 'Limited cards', cards_only: 'Cards-only privacy' })
          .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </TextField>
      <TextField select disabled={running} size="small" label="Feature" value={feature} onChange={e => { setFeature(e.target.value); setReport(null); setError(false); }} sx={{ minWidth: 150 }}>
        {['profile', 'chat', 'suggestions'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <TextField select disabled={running} size="small" label="Tier model" value={tier} onChange={e => { setTier(e.target.value); setReport(null); setError(false); }} sx={{ minWidth: 140 }}>
        {['ember', 'flame', 'blaze'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <Button variant="outlined" disabled={running} onClick={async () => {
        setRunning(true); setError(false); setReport(null);
        // ApiClient counts total attempts, so one sends once without an automatic retry.
        try { setReport(await apiClient.post<ComparisonReport>('/admin/ai-config/personalization/validate', { scenario, feature, tier }, { timeout: 60_000, retries: 1 })); }
        catch (failure) { logger.error('Context comparison failed', failure); setError(true); }
        finally { setRunning(false); }
      }}>{running ? 'Comparing…' : 'Run comparison'}</Button>
    </Box>
    {error && <Alert severity="error">Comparison failed. Try again after checking the configured provider.</Alert>}
    {report && <Box aria-label="User context comparison results" display="grid" gap={2} sx={{ gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
      {(['baseline', 'current'] as const).map(side => <Box key={side} p={2} bgcolor="action.hover" sx={{ overflowWrap: 'anywhere' }}>
        <Typography variant="h6">{side === 'baseline' ? 'Published baseline' : 'Current implementation'}</Typography>
        {report[side].unavailable && <Alert severity="info">{report[side].unavailable}</Alert>}
        {report[side].error && <Alert severity="error">{report[side].error}</Alert>}
        {report[side].output && <Answer value={report[side].output!} />}
        {report[side].model && <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          {report[side].model} · {(report[side].latencyMs / 1000).toFixed(2)} s ·
          {' '}{report[side].inputTokens ?? '—'} input / {report[side].outputTokens ?? '—'} output tokens ·
          {' '}{report[side].costUSD === null ? 'Cost unavailable' : '$' + report[side].costUSD!.toFixed(6)}
        </Typography>}
      </Box>)}
    </Box>}
  </Box>;
}
